import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,

  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CardActions
} from '@mui/material';
import Grid from '../common/Grid';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Science as ScienceIcon,
  ArrowForward as ArrowForwardIcon,
  Verified as VerifiedIcon,
  LocalFlorist as CropIcon
} from '@mui/icons-material';
import { setFormulation, WorkflowStage, WorkflowFormulation } from '../../store/slices/workflowSlice';

interface DatabaseFormulation {
  id: string;
  name: string;
  description?: string;
  cropType?: string;
  targetEc?: number;
  targetPh?: number;
  volume: number;
  volumeUnit: string;
  createdAt: string;
  targetElements: {
    element: {
      symbol: string;
      name: string;
    };
    concentration: number;
    unit: string;
  }[];
}

interface AnalysisResult {
  overallScore: number;
  elementBalance: 'excellent' | 'good' | 'fair' | 'poor';
  npkRatio: string;
  essentialElements: {
    symbol: string;
    present: boolean;
    concentration?: number;
    status: 'optimal' | 'acceptable' | 'low' | 'missing';
  }[];
  recommendations: string[];
  compatibility: 'excellent' | 'good' | 'fair' | 'poor';
  scientificReference?: string;
}

const FormulationAnalysis: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [formulations, setFormulations] = useState<DatabaseFormulation[]>([]);
  const [selectedFormulationId, setSelectedFormulationId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Essential elements for hydroponic cultivation
  const essentialElements = [
    { symbol: 'N', name: 'Nitrogen', optimalRange: { min: 100, max: 250 } },
    { symbol: 'P', name: 'Phosphorus', optimalRange: { min: 30, max: 80 } },
    { symbol: 'K', name: 'Potassium', optimalRange: { min: 150, max: 350 } },
    { symbol: 'Ca', name: 'Calcium', optimalRange: { min: 120, max: 200 } },
    { symbol: 'Mg', name: 'Magnesium', optimalRange: { min: 40, max: 80 } },
    { symbol: 'S', name: 'Sulfur', optimalRange: { min: 50, max: 100 } },
    { symbol: 'Fe', name: 'Iron', optimalRange: { min: 2, max: 5 } },
    { symbol: 'Mn', name: 'Manganese', optimalRange: { min: 0.5, max: 2 } },
    { symbol: 'Zn', name: 'Zinc', optimalRange: { min: 0.2, max: 1 } },
    { symbol: 'Cu', name: 'Copper', optimalRange: { min: 0.05, max: 0.2 } },
    { symbol: 'B', name: 'Boron', optimalRange: { min: 0.3, max: 1 } },
    { symbol: 'Mo', name: 'Molybdenum', optimalRange: { min: 0.01, max: 0.1 } }
  ];

  useEffect(() => {
    fetchFormulations();
  }, []);

  const fetchFormulations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/formulations?includeDetails=true');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const formulationsData = await response.json();
      console.log('FormulationAnalysis - Fetched formulations data:', formulationsData);

      // Extract the data array from the response
      const formulationsArray = formulationsData.data && Array.isArray(formulationsData.data)
        ? formulationsData.data
        : [];

      console.log('FormulationAnalysis - Formulations array:', formulationsArray);
      console.log('FormulationAnalysis - First formulation target elements:', formulationsArray[0]?.targetElements);

      setFormulations(formulationsArray);

      // Auto-select first formulation if available
      if (formulationsArray.length > 0 && !selectedFormulationId) {
        setSelectedFormulationId(formulationsArray[0].id);
        analyzeFormulation(formulationsArray[0]);
      }
    } catch (error) {
      console.error('Error fetching formulations:', error);
      setError('Failed to load formulations from database');
    } finally {
      setLoading(false);
    }
  };

  const handleFormulationChange = (formulationId: string) => {
    setSelectedFormulationId(formulationId);
    const formulation = formulations.find(f => f.id === formulationId);
    if (formulation) {
      analyzeFormulation(formulation);
    }
  };

  const analyzeFormulation = async (formulation: DatabaseFormulation) => {
    setAnalyzing(true);
    
    try {
      // Simulate analysis delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const analysis = performAnalysis(formulation);
      setAnalysisResult(analysis);
    } catch (error) {
      console.error('Error analyzing formulation:', error);
      setError('Failed to analyze formulation');
    } finally {
      setAnalyzing(false);
    }
  };

  const performAnalysis = (formulation: DatabaseFormulation): AnalysisResult => {
    const elements = formulation.targetElements;
    
    // Calculate NPK ratio
    const n = elements.find(e => e.element.symbol === 'N')?.concentration || 0;
    const p = elements.find(e => e.element.symbol === 'P')?.concentration || 0;
    const k = elements.find(e => e.element.symbol === 'K')?.concentration || 0;
    
    const npkRatio = calculateNPKRatio(n, p, k);
    
    // Analyze essential elements
    const essentialElementsAnalysis = essentialElements.map(essential => {
      const element = elements.find(e => e.element.symbol === essential.symbol);
      if (!element) {
        return {
          symbol: essential.symbol,
          present: false,
          status: 'missing' as const
        };
      }
      
      const concentration = element.concentration;
      const { min, max } = essential.optimalRange;
      
      let status: 'optimal' | 'acceptable' | 'low' | 'missing';
      if (concentration >= min && concentration <= max) {
        status = 'optimal';
      } else if (concentration >= min * 0.7 && concentration <= max * 1.3) {
        status = 'acceptable';
      } else if (concentration < min) {
        status = 'low';
      } else {
        status = 'acceptable'; // High but acceptable
      }
      
      return {
        symbol: essential.symbol,
        present: true,
        concentration,
        status
      };
    });
    
    // Calculate overall score
    const presentElements = essentialElementsAnalysis.filter(e => e.present).length;
    const optimalElements = essentialElementsAnalysis.filter(e => e.status === 'optimal').length;
    const acceptableElements = essentialElementsAnalysis.filter(e => e.status === 'acceptable').length;
    
    const completenessScore = (presentElements / essentialElements.length) * 100;
    const qualityScore = ((optimalElements * 1.0 + acceptableElements * 0.7) / essentialElements.length) * 100;
    const overallScore = Math.round((completenessScore + qualityScore) / 2);
    
    // Determine element balance
    let elementBalance: 'excellent' | 'good' | 'fair' | 'poor';
    if (overallScore >= 90) elementBalance = 'excellent';
    else if (overallScore >= 75) elementBalance = 'good';
    else if (overallScore >= 60) elementBalance = 'fair';
    else elementBalance = 'poor';
    
    // Generate recommendations
    const recommendations = generateRecommendations(formulation, essentialElementsAnalysis);
    
    // Determine compatibility
    const compatibility = getCompatibilityRating(overallScore);
    
    return {
      overallScore,
      elementBalance,
      npkRatio,
      essentialElements: essentialElementsAnalysis,
      recommendations,
      compatibility,
      scientificReference: getScientificReference(formulation)
    };
  };

  const calculateNPKRatio = (n: number, p: number, k: number): string => {
    if (n === 0 && p === 0 && k === 0) return 'N/A';
    
    const min = Math.min(n, p, k);
    if (min === 0) return `${n.toFixed(0)}-${p.toFixed(0)}-${k.toFixed(0)}`;
    
    return `${(n/min).toFixed(1)}-${(p/min).toFixed(1)}-${(k/min).toFixed(1)}`;
  };

  const generateRecommendations = (formulation: DatabaseFormulation, elementAnalysis: any[]): string[] => {
    const recommendations = [];
    
    const missingElements = elementAnalysis.filter(e => !e.present);
    if (missingElements.length > 0) {
      recommendations.push(`Add missing essential elements: ${missingElements.map(e => e.symbol).join(', ')}`);
    }
    
    const lowElements = elementAnalysis.filter(e => e.status === 'low');
    if (lowElements.length > 0) {
      recommendations.push(`Increase concentrations for: ${lowElements.map(e => e.symbol).join(', ')}`);
    }
    
    const npkElements = elementAnalysis.filter(e => ['N', 'P', 'K'].includes(e.symbol) && e.present);
    if (npkElements.length < 3) {
      recommendations.push('Ensure all primary macronutrients (N, P, K) are included');
    }
    
    const microElements = elementAnalysis.filter(e => ['Fe', 'Mn', 'Zn', 'Cu', 'B', 'Mo'].includes(e.symbol) && e.present);
    if (microElements.length < 4) {
      recommendations.push('Consider adding more micronutrients for optimal plant health');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('Formulation appears well-balanced for hydroponic cultivation');
    }
    
    return recommendations;
  };

  const getCompatibilityRating = (score: number): 'excellent' | 'good' | 'fair' | 'poor' => {
    if (score >= 90) return 'excellent';
    if (score >= 75) return 'good';
    if (score >= 60) return 'fair';
    return 'poor';
  };

  const getScientificReference = (formulation: DatabaseFormulation): string => {
    // Check if it's a standard formulation based on name
    if (formulation.name.toLowerCase().includes('resh')) {
      return 'Howard M. Resh - Hydroponic Food Production, 7th Edition';
    }
    if (formulation.name.toLowerCase().includes('hoagland')) {
      return 'Hoagland and Arnon - The water-culture method for growing plants';
    }
    return 'User-defined formulation - verify against scientific literature';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal': return 'success';
      case 'acceptable': return 'info';
      case 'low': return 'warning';
      case 'missing': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'optimal': return <CheckIcon />;
      case 'acceptable': return <InfoIcon />;
      case 'low': return <WarningIcon />;
      case 'missing': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  const handleConfirmFormulation = async () => {
    if (!selectedFormulationId || !analysisResult) {
      return;
    }

    setConfirming(true);

    try {
      const selectedFormulation = formulations.find(f => f.id === selectedFormulationId);
      if (!selectedFormulation) {
        throw new Error('Selected formulation not found');
      }

      // Convert formulation to workflow format
      const workflowFormulation: WorkflowFormulation = {
        id: selectedFormulation.id,
        name: selectedFormulation.name,
        description: selectedFormulation.description,
        cropType: selectedFormulation.cropType,
        targetEc: selectedFormulation.targetEc,
        targetPh: selectedFormulation.targetPh,
        volume: selectedFormulation.volume,
        volumeUnit: selectedFormulation.volumeUnit,
        elements: selectedFormulation.targetElements.map(element => ({
          symbol: element.element.symbol,
          concentration: element.concentration, // Fixed: changed from targetConcentration to concentration
          unit: element.unit
        })),
        analysisResults: {
          qualityScore: analysisResult.overallScore,
          recommendations: analysisResult.recommendations,
          compatibility: analysisResult.compatibility
        },
        scientificReference: analysisResult.scientificReference,
        ratioAnalysis: {
          npkRatio: analysisResult.npkRatio,
          micronutrientBalance: analysisResult.elementBalance === 'excellent' ? 'optimal' :
                               analysisResult.elementBalance === 'good' ? 'adequate' : 'deficient'
        }
      };

      // Save to workflow state
      dispatch(setFormulation(workflowFormulation));

      // Navigate to next stage
      navigate('/salt-library');
      
    } catch (error) {
      console.error('Error confirming formulation:', error);
      setError(`Failed to confirm formulation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading formulations...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (formulations.length === 0) {
    return (
      <Alert severity="info">
        No formulations found in database. Create a formulation first to analyze it.
      </Alert>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Formulation Analysis & Reports
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Formulation to Analyze</InputLabel>
            <Select
              value={selectedFormulationId}
              onChange={(e) => handleFormulationChange(e.target.value)}
              label="Select Formulation to Analyze"
            >
              {formulations.map((formulation) => (
                <MenuItem key={formulation.id} value={formulation.id}>
                  {formulation.name} ({formulation.cropType || 'General'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {analyzing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Analyzing formulation...
              </Typography>
              <CircularProgress size={20} />
            </Box>
          )}
        </CardContent>
      </Card>

      {analysisResult && (
        <Grid container spacing={3}>
          {/* Overall Score */}
          <Grid xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Formulation Score
                </Typography>
                <Typography variant="h2" color="primary">
                  {analysisResult.overallScore}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  out of 100
                </Typography>
                <Chip
                  label={analysisResult.compatibility.toUpperCase()}
                  color={getStatusColor(analysisResult.compatibility) as any}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Formulation Details */}
          <Grid xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Formulation Analysis
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography variant="body2">
                      <strong>NPK Ratio:</strong> {analysisResult.npkRatio}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="body2">
                      <strong>Element Balance:</strong>
                      <Chip
                        label={analysisResult.elementBalance}
                        color={getStatusColor(analysisResult.elementBalance) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Essential Elements Status:
                </Typography>
                
                <Grid container spacing={1}>
                  {analysisResult.essentialElements.map((element, index) => (
                    <Grid xs={6} sm={4} md={3} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getStatusIcon(element.status)}
                        <Typography variant="caption">
                          {element.symbol}: {element.present ? `${element.concentration} ppm` : 'Missing'}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {analysisResult.scientificReference && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Scientific Reference:</strong> {analysisResult.scientificReference}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations */}
          <Grid xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Recommendations
                </Typography>

                <List>
                  {analysisResult.recommendations.map((recommendation, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={recommendation} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Workflow Confirmation */}
          <Grid xs={12}>
            <Card sx={{ 
              border: '2px solid', 
              borderColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good' 
                ? 'success.main' 
                : 'warning.main',
              backgroundColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                ? 'success.50'
                : 'warning.50'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <VerifiedIcon 
                    color={analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good' 
                      ? 'success' 
                      : 'warning'
                    } 
                    sx={{ fontSize: 32 }}
                  />
                  <Box>
                    <Typography variant="h6">
                      Formulation Analysis Complete
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formulations.find(f => f.id === selectedFormulationId)?.name} - 
                      Score: {analysisResult.overallScore}/100 ({analysisResult.compatibility.toUpperCase()})
                    </Typography>
                  </Box>
                </Box>

                <Alert 
                  severity={analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good' 
                    ? 'success' 
                    : 'warning'
                  } 
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    {analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good' 
                      ? '✅ This formulation is well-balanced for hydroponic cultivation and will be used for nutrient calculations.'
                      : '⚠️ This formulation has some limitations but can still be used. Please review the recommendations above.'
                    }
                  </Typography>
                </Alert>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Click "Confirm & Continue" to save this formulation to your workflow and proceed to salt library selection.
                </Typography>
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedFormulationId('');
                    setAnalysisResult(null);
                  }}
                  disabled={confirming}
                >
                  Select Different Formulation
                </Button>
                
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleConfirmFormulation}
                  disabled={confirming || !selectedFormulationId || !analysisResult}
                  endIcon={confirming ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                  sx={{ 
                    minWidth: 200,
                    backgroundColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good' 
                      ? 'success.main' 
                      : 'warning.main',
                    '&:hover': {
                      backgroundColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good' 
                        ? 'success.dark' 
                        : 'warning.dark'
                    }
                  }}
                >
                  {confirming ? 'Confirming...' : 'Confirm & Continue to Salt Library'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FormulationAnalysis;
